import Controller from "./application_controller"

/**
 * Delegated checkbox selection for a table/list with many rows.
 *
 * A single listener on the container handles row checkbox toggles, a select-all
 * checkbox, and shift-click range selection — so rows can be added or removed
 * without any per-row wiring. Toggling a row marks its row element with the
 * `selected` class and mirrors the checkbox, keeps the select-all checkbox in
 * sync, then fires a `row-selection:change` event with the selected row count
 * for other code (e.g. action buttons) to react to.
 *
 * Code that adds or removes rows outside this controller can dispatch a
 * `row-selection:refresh` event on the element to trigger the same re-sync.
 *
 *   <table data-controller="row-selection"
 *          data-row-selection-row-value="tr.item"
 *          data-row-selection-selected-class="selected"
 *          data-action="row-selection:refresh->row-selection#refresh">
 *     <input type="checkbox" data-row-selection-target="all">  <!-- select all -->
 *     <tr class="item"><input type="checkbox"></tr>
 *   </table>
 */
export default class extends Controller {
  static targets = ["all"]
  static classes = ["selected"]
  static values = { row: { type: String, default: "tr" } }

  connect() {
    this.lastRow = null
    this.addEventListener("click", this.onClick.bind(this))
  }

  onClick(event) {
    const input = event.target
    if (input.type !== "checkbox") return

    if (this.hasAllTarget && input === this.allTarget) {
      this.selectAll(input.checked)
    } else if (input.closest(this.rowValue)) {
      this.toggleRow(input, event.shiftKey)
    }
  }

  selectAll(selected) {
    this.rows.forEach((row) => this.setRowSelected(row, selected))
    this.notifyChange()
  }

  toggleRow(checkbox, shiftKey) {
    const row = checkbox.closest(this.rowValue)
    const selected = checkbox.checked
    row.classList.toggle(this.selectedClass, selected)

    if (shiftKey && this.lastRow && this.lastRow !== row) {
      const rows = this.rows
      const from = rows.indexOf(this.lastRow)
      const to = rows.indexOf(row)

      if (from !== -1 && to !== -1) {
        for (let i = Math.min(from, to); i <= Math.max(from, to); i++) {
          this.setRowSelected(rows[i], selected)
        }
      }
    }

    this.lastRow = row
    this.notifyChange()
  }

  setRowSelected(row, selected) {
    row.classList.toggle(this.selectedClass, selected)
    const checkbox = row.querySelector('input[type="checkbox"]')
    if (checkbox) checkbox.checked = selected
  }

  refresh() {
    this.notifyChange()
  }

  notifyChange() {
    const rows = this.rows
    this.syncAllTarget(rows)

    const count = rows.filter((row) => row.classList.contains(this.selectedClass)).length
    this.dispatch("change", { detail: { count } })
  }

  syncAllTarget(rows) {
    if (!this.hasAllTarget) return

    const nextChecked =
      rows.length > 0 && rows.every((row) => row.classList.contains(this.selectedClass))

    if (this.allTarget.checked !== nextChecked) {
      this.allTarget.checked = nextChecked
    }
  }

  get rows() {
    return Array.from(this.element.querySelectorAll(this.rowValue))
  }
}
